import api from "./client";

import type { ApiResponse, BaseFilterParams, Paginated } from "#/types/api";
import type {
  CreateItemPayload,
  Item,
  MapData,
  SendMessagePayload,
} from "#/types/items";
import { type ScanHistory } from "#/types/scan-history";

export const itemsApi = {
  create: (body: Partial<CreateItemPayload>): Promise<ApiResponse<Item>> =>
    api.post("/item", body),
  update: (
    id: number,
    body: Partial<CreateItemPayload>,
  ): Promise<ApiResponse<Item>> => api.patch(`/item/${id}`, body),
  getAll: (
    params?: BaseFilterParams,
  ): Promise<ApiResponse<Paginated<Item[]>>> => api.get("/item", { params }),
  getById: (id: string): Promise<ApiResponse<Item>> => api.get(`/item/${id}`),
  sendMessage: (
    id: number,
    body: SendMessagePayload,
  ): Promise<ApiResponse<Item>> =>
    api.post(`/item/${id}/notify-manufacturer`, body),
  uploadCsvFile: (
    file: File,
    onProgress?: (progress: number) => void,
  ): Promise<
    ApiResponse<{
      success: Item[];
      errors: { row: number; data: Record<string, string>; error: string }[];
    }>
  > =>
    api.uploadFile<
      ApiResponse<{
        success: Item[];
        errors: { row: number; data: Record<string, string>; error: string }[];
      }>
    >("/item/csv/import", file, "file", onProgress),
  getAUM: (): Promise<
    ApiResponse<{
      total: number;
      byProduct: Array<{
        productId: number;
        productName: string;
        totalValue: number;
      }>;
      bySite: Array<{
        siteId: number;
        siteName: string;
        totalValue: number;
      }>;
    }>
  > => api.get("/item/aum"),
  getMapData: (): Promise<ApiResponse<MapData>> => api.get("/item/map"),
  getHistory: (
    id: number,
    type: "scan" | "refill",
    params: BaseFilterParams,
  ): Promise<ApiResponse<Paginated<ScanHistory[]>>> =>
    api.get(`/item/${id}/${type}/history`, { params }),
  bulkCreateFromTemplate: (
    templateId: number,
    rfidIds: number[],
  ): Promise<ApiResponse<Item[]>> =>
    api.post("/item/bulk-create-from-template", {
      template_id: templateId,
      rfid_ids: rfidIds,
    }),
  bulkUpdateStatus: (
    itemIds: number[],
    status: string,
  ): Promise<ApiResponse<{ updated: number }>> =>
    api.patch("/item/bulk-update-status", {
      item_ids: itemIds,
      status: status,
    }),
  bulkDelete: (
    itemIds: number[],
  ): Promise<ApiResponse<{ deleted: number }>> =>
    api.delete("/item/bulk-delete", {
      data: { item_ids: itemIds },
    }),
  getAvailableCageItems: (params?: {
    search?: string;
    cage_id?: number;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<Paginated<Item[]>>> =>
    api.get("/item/available-cage-items", { params }),
};
